/**
 * In-memory cache used for caching parts of the app that are expensive
 * to calculate. The cache should be cleared whenever pages get updated,
 * which happens via /api/invalidate.
 */
import { LRUCache } from "lru-cache";

const ttl = 5 * 60 * 1_000; // 5 minutes

const cache = new LRUCache<string, any>({
  max: 50,
  ttl,
});

const inflight = new Map<string, Promise<any>>();

export enum SiteCacheItem {
  FAQ_URL_STRUCTURE = "FAQ_URL_STRUCTURE",
  PRESS_RELEASE_LIST = "PRESS_RELEASE_LIST",
  PRESS_RELEASE_STRUCTURE = "PRESS_RELEASE_STRUCTURE",
  PRESS_RELEASE_URL_STRUCTURE = "PRESS_RELEASE_URL_STRUCTURE",
  SIDE_BAR = "SIDE_BAR",
  SITE_FLAT_STRUCTURE = "SITE_FLAT_STRUCTURE",
  SITE_TREE = "SITE_TREE",
  URL_STRUCTURE = "URL_STRUCTURE",
  URL_STRUCTURE_ITEMS = "URL_STRUCTURE_ITEMS",
  GLOBAL_SETTINGS = "GLOBAL_SETTINGS",
  PAYMENT_INFORMATION_BLOCK = "PAYMENT_INFORMATION_BLOCK",
}

export const siteCacheGet = <T>(key: SiteCacheItem): T | null => {
  if (process.env.DISABLE_SITE_CACHE === "true") {
    return null;
  }
  return (cache.get(key) as T) ?? null;
};

export const siteCacheSet = (key: SiteCacheItem, data: any): void => {
  cache.set(key, data);
};

export const siteCacheClear = (): void => {
  cache.clear();
  inflight.clear();
};

/**
 * Fetch with singleflight: if a fetch for the same key is already in progress,
 * all callers share the same promise. Failed fetches are NOT cached – the next
 * caller retries.
 */
export const siteCacheFetch = async <T>(
  key: SiteCacheItem,
  fetcher: () => Promise<T>,
): Promise<T> => {
  if (process.env.DISABLE_SITE_CACHE === "true") {
    return fetcher();
  }

  const cached = cache.get(key) as T | undefined;
  if (cached !== undefined) {
    return cached;
  }

  const existing = inflight.get(key);
  if (existing) {
    return existing as Promise<T>;
  }

  const promise = fetcher()
    .then((result) => {
      cache.set(key, result);
      return result;
    })
    .finally(() => {
      inflight.delete(key);
    });

  inflight.set(key, promise);
  return promise;
};
